import { format, isToday, isValid, isYesterday } from 'date-fns';
import { useLocalSearchParams } from 'expo-router';
import { Dna, HatGlasses, type LucideIcon } from 'lucide-react-native';
import { useMemo } from 'react';
import { View } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { primum_default_avatar } from '@/assets/images';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Badge, BadgeText } from '@/components/ui/badge';
import { Box } from '@/components/ui/box';
import { Card } from '@/components/ui/card';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { SkeletonText } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { ANONYMOUS_PRIMUM_ROUTING_INBOX_HINT } from '@/constants/inquiryDisplay';
import { useInboxQuestionGroupCardsEnabled } from '@/hooks/inbox/useInboxQuestionGroups';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useIsCompactLayout } from '@/hooks/useResponsiveLayout';
import type { InquiryV2 } from '@/services/getInquiriesV2';
import type { InquiryLabel } from '@/services/inquiryLabels';
import { getNativeTextProps } from '@/util/getNativeTextProps';
import { parseGroupedMessagePreview } from '@/util/groupedMessagePreview';
import { getInquiryGroupStateBadges, type InquiryGroupStateCounts } from '@/util/inquiryGroups';
import { formatLabelText } from '@/util/inquiryLabelHelpers';

/**
 * Tone separates the two kinds in the badge family. `info` marks a route the submitter chose,
 * `muted` marks the absence of a choice, so the grouping reads without relying on color alone.
 */
export type InquiryTypeBadge = {
  label: string;
  icon: LucideIcon;
  tone: 'info' | 'muted';
};

const TYPE_BADGE_TONE: Record<InquiryTypeBadge['tone'], string> = {
  info: 'text-info-700',
  muted: 'text-typography-700',
};

type InquiryStatus = InquiryV2['inquiryStatus'];

/**
 * The drafts list reuses this card and has no API inquiry behind it, so it
 * passes a status the contract does not define. That value never reaches the
 * status badge, which is hidden on the drafts tab.
 */
type MessageCardInquiryStatus = InquiryStatus | 'draft';

/**
 * Badge copy per inquiry status.
 *
 * Keyed on the API's own status union rather than `string`, so a status added
 * to the contract fails the build here instead of reaching the renderer
 * unmapped. It did once: `initializing` was absent, and because the lookup was
 * typed as returning `string` while a `Record<string, …>` index actually
 * yields `string | undefined`, the miss crashed the whole inbox to the
 * not-found screen the first time a freshly created inquiry rendered.
 *
 * `initializing` reads as `open` because it is an inquiry the submitter has
 * already sent, still waiting on backend provisioning — a distinction with no
 * meaning to the reader.
 */
const INQUIRY_STATUS_DISPLAY: Record<InquiryStatus, string> = {
  active: 'open',
  initializing: 'open',
  inactive: 'close requested',
  closed: 'closed',
};

const getInquiryStatusDisplay = (status: MessageCardInquiryStatus): string | undefined => {
  return INQUIRY_STATUS_DISPLAY[status as InquiryStatus];
};

const capitalizeFirst = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

function formatMessageCardDateLabel(lastRespondedAt: Date | string): string {
  const date = lastRespondedAt instanceof Date ? lastRespondedAt : new Date(lastRespondedAt);

  if (!isValid(date)) {
    return '';
  }

  if (isToday(date)) {
    return format(date, 'h:mm a');
  }

  if (isYesterday(date)) {
    return 'Yesterday';
  }

  return format(date, 'MMM d');
}

/**
 * Outlines the row's status badges. `ui/badge` declares a border color per
 * action but only gives the outline variant a width, so a solid badge never
 * paints one. That is invisible everywhere else and a problem here: an "Open"
 * badge is `action="info"`, which fills with `background-info` — the exact
 * color an unread row is tinted — so on the rows that matter most the badge
 * dissolves into its own background.
 *
 * Scoped to this card rather than fixed in `ui/badge`, since adding width there
 * would switch on borders for every solid badge in the app at once.
 */
const STATUS_BADGE_OUTLINE = 'border';

export const AVATAR_SLOT_SIZE = 44;

const TWO_EXPERT_AVATAR_SIZE = 24;

const THREE_EXPERT_AVATAR_SIZE = 22;

const TWO_EXPERT_AVATAR_POSITIONS = [
  { left: 1, top: 1 },
  { left: 19, top: 19 },
] as const;

const THREE_EXPERT_AVATAR_POSITIONS = [
  { left: 11, top: 1 },
  { left: 0, top: 21 },
  { left: 22, top: 21 },
] as const;

export const getGroupedAvatarLayout = (index: number, total: number) => {
  if (total === 2) {
    const position =
      TWO_EXPERT_AVATAR_POSITIONS[Math.min(index, TWO_EXPERT_AVATAR_POSITIONS.length - 1)];

    return { ...position, size: TWO_EXPERT_AVATAR_SIZE };
  }

  const position =
    THREE_EXPERT_AVATAR_POSITIONS[Math.min(index, THREE_EXPERT_AVATAR_POSITIONS.length - 1)];

  return { ...position, size: THREE_EXPERT_AVATAR_SIZE };
};

function getMessageCardSecondaryNameText(options: {
  displayName: string;
  groupExpertNamesInline: string | undefined;
  showInlineExperts: boolean;
}) {
  const { displayName, groupExpertNamesInline, showInlineExperts } = options;

  if (!showInlineExperts || !groupExpertNamesInline) {
    return displayName;
  }

  return groupExpertNamesInline;
}

/**
 * A row's state as a background fill only, with no accent rail: gray when selected, a lighter gray
 * on hover, and an info tint when unread.
 *
 * Where the inbox shows inquiry group cards, an unread row takes no tint. The cards mark unread with
 * the dot and a badge alone, and a list that tinted its single threads but not its cards showed the
 * same state two ways. The dot and the bolder preview still mark the row.
 */
export function getMessageCardStateClass({
  isSelected,
  showUnreadState,
  isQuestionGroupCards,
}: {
  isSelected: boolean;
  showUnreadState: boolean;
  isQuestionGroupCards: boolean;
}) {
  if (isSelected) {
    return 'bg-background-100';
  }

  if (showUnreadState && !isQuestionGroupCards) {
    return 'bg-background-info hover:bg-info-50 active:bg-info-100';
  }

  return 'hover:bg-background-50 active:bg-background-100';
}

export function MessageCard({
  subject = '--',
  name = ' ',
  nameWithoutDoctor,
  avatarUrl,
  lastRespondedAt,
  message = ' ',
  unread,
  isMessageLoaded = true,
  isUserLoaded = true,
  inquiryStatus,
  onPress,
  id,
  currentTab,
  isPrimum,
  productLabel,
  typeBadge,
  labels,
  inquiryGroupCount,
  inquiryGroupVisibleCount,
  inquiryGroupStateBreakdown,
  hideSubject,
  hideName,
  showSupportBadgeInline,
  hideTimestamp,
  groupExperts,
  routeLabels,
  selectedConversationIds,
  anonymousPrimumRouting,
  askAnonymouslyRelayView,
  showGroupedAnonymousHint,
}: {
  id?: string;
  subject?: string;
  lastRespondedAt: Date;
  name?: string;
  nameWithoutDoctor?: string;
  avatarUrl?: string;
  message?: string;
  unread?: boolean;
  isUserLoaded?: boolean;
  isMessageLoaded?: boolean;
  inquiryStatus: MessageCardInquiryStatus;
  onPress: () => void;
  currentTab: string;
  isPrimum?: boolean;
  productLabel?: string;
  /**
   * Short badge naming the kind of inquiry, for kinds that are not obvious from the participants.
   * Carries its own icon and tone: the family holds more than one kind, and a label alone would
   * make every member wear the first member's appearance.
   */
  typeBadge?: InquiryTypeBadge;
  labels?: InquiryLabel[];
  inquiryGroupCount?: number;
  inquiryGroupVisibleCount?: number;
  inquiryGroupStateBreakdown?: InquiryGroupStateCounts;
  hideSubject?: boolean;
  hideName?: boolean;
  showSupportBadgeInline?: boolean;
  hideTimestamp?: boolean;
  groupExperts?: { name: string; avatarUrl?: string; variant?: 'route' }[];
  /** MOD recipient view: route labels (J&J MSL / J&J Medical Information Team)
   * to surface which routes this group spans, rendered as small chips. */
  routeLabels?: string[];
  selectedConversationIds?: string[];
  /** True when this is an anonymous Ask Anonymously conversation, not a direct expert conversation. */
  anonymousPrimumRouting?: boolean;
  /** True when the expert/support side is viewing the thread, so submitter-only hints stay hidden. */
  askAnonymouslyRelayView?: boolean;
  /** True when the submitter should see grouped anonymous privacy hint copy on the inbox card. */
  showGroupedAnonymousHint?: boolean;
}) {
  const params = useLocalSearchParams<{ id?: string }>();

  const selectedChatId = params.id === 'undefined' ? undefined : params.id;

  const isCompactLayout = useIsCompactLayout();

  const formattedDate = lastRespondedAt ? formatMessageCardDateLabel(lastRespondedAt) : '';

  const isUnassigned =
    currentTab === 'drafts' && (!name || name === 'No expert selected' || name === ' ');

  const hasGroupedExperts = (groupExperts?.length ?? 0) > 1;

  const isRouteGroup =
    hasGroupedExperts && groupExperts!.every((expert) => expert.variant === 'route');

  const showGroupedExpertAvatars = hasGroupedExperts && !askAnonymouslyRelayView && !isRouteGroup;

  const displayName = isUnassigned ? 'Unassigned' : name;

  const visibleGroupExperts = hasGroupedExperts ? groupExperts!.slice(0, 3) : [];

  const groupExpertNamesInline = hasGroupedExperts
    ? groupExperts!
        .map((expert) => expert.name)
        .filter(Boolean)
        .join(', ')
    : undefined;

  const showInlineExperts = hasGroupedExperts;

  const effectiveAvatarUrl = isRouteGroup
    ? (groupExperts!.find((expert) => expert.avatarUrl)?.avatarUrl ?? avatarUrl)
    : avatarUrl;

  const secondaryNameText = getMessageCardSecondaryNameText({
    displayName,
    groupExpertNamesInline,
    showInlineExperts,
  });

  const parsedPreview = useMemo(() => {
    return parseGroupedMessagePreview(message, hasGroupedExperts);
  }, [hasGroupedExperts, message]);

  const groupStateBadges = useMemo(() => {
    return getInquiryGroupStateBadges(inquiryGroupStateBreakdown);
  }, [inquiryGroupStateBreakdown]);

  const showStatusBadge = currentTab !== 'drafts' && currentTab !== 'closed';

  const inquiryStatusBadgeAction = inquiryStatus === 'closed' ? 'muted' : 'info';

  const isInquiryLabelsEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_LABELS);

  const visibleLabels = useMemo(() => {
    if (!isInquiryLabelsEnabled) {
      return [];
    }

    const filtered = (labels ?? []).filter((label) => {
      const text = formatLabelText(label).trim();

      return text.length > 0;
    });

    return filtered;
  }, [labels, id, currentTab, isInquiryLabelsEnabled]);

  const isDirectlySelected = selectedChatId === id;

  const isGroupMemberSelected =
    selectedChatId !== undefined && selectedConversationIds?.includes(selectedChatId);

  const isSelected = isDirectlySelected || isGroupMemberSelected;

  const showUnreadState = Boolean(unread) && !isSelected;

  const isQuestionGroupCardsEnabled = useInboxQuestionGroupCardsEnabled();

  const renderStatusBadge = (keySuffix?: string) => {
    if (groupStateBadges.length) {
      return (
        <VStack className="items-end gap-1">
          {groupStateBadges.map((badge) => (
            <Badge
              key={`${badge.label}-${badge.action}${keySuffix ?? ''}`}
              size="md"
              action={badge.action}
              variant="solid"
              className={STATUS_BADGE_OUTLINE}>
              <BadgeText size="sm" className="normal-case">
                {capitalizeFirst(badge.label)}
              </BadgeText>
            </Badge>
          ))}
        </VStack>
      );
    }

    if (!showStatusBadge) {
      return null;
    }

    const statusLabel = getInquiryStatusDisplay(inquiryStatus);

    // A status with no copy has nothing to render, and a badge is not worth
    // taking the screen down over.
    if (!statusLabel) {
      return null;
    }

    return (
      <Badge
        size="md"
        action={inquiryStatusBadgeAction}
        variant="solid"
        className={STATUS_BADGE_OUTLINE}>
        <BadgeText size="sm" className="normal-case">
          {capitalizeFirst(statusLabel)}
        </BadgeText>
      </Badge>
    );
  };

  const statusBadge = renderStatusBadge();

  const desktopStatusBadge = renderStatusBadge('-desktop-status');

  const cardStateClass = getMessageCardStateClass({
    isSelected: Boolean(isSelected),
    showUnreadState,
    isQuestionGroupCards: isQuestionGroupCardsEnabled,
  });

  return (
    <Pressable
      testID={id ? `inquiry-card-${id}` : undefined}
      className={twJoin(
        'web:transition-colors web:duration-150 data-[focus-visible=true]:web:ring-inset',
        cardStateClass
      )}
      onPress={onPress}>
      <Card size={isCompactLayout ? 'md' : 'sm'} variant="ghost" className="py-4 pl-0 pr-5 md:pl-0">
        <VStack>
          {isCompactLayout ? (
            <HStack className="items-start">
              <HStack className="w-[68px] shrink-0 items-start gap-1 pt-1">
                <Box className="w-4 items-center self-center">
                  <Box
                    testID={showUnreadState ? 'unread-indicator' : undefined}
                    className={twJoin(
                      'h-2.5 w-2.5',
                      showUnreadState && 'rounded-full bg-tertiary-400'
                    )}
                  />
                </Box>
                {showGroupedExpertAvatars ? (
                  <Box
                    className="relative"
                    style={{ width: AVATAR_SLOT_SIZE, height: AVATAR_SLOT_SIZE }}>
                    {visibleGroupExperts.map((expert, index) => {
                      const layout = getGroupedAvatarLayout(index, visibleGroupExperts.length);

                      return (
                        <Avatar
                          key={`${expert.name}-${index}`}
                          size="xs"
                          className="absolute"
                          style={{
                            left: layout.left,
                            top: layout.top,
                            width: layout.size,
                            height: layout.size,
                          }}>
                          <AvatarImage
                            source={
                              expert.avatarUrl ? { uri: expert.avatarUrl } : primum_default_avatar
                            }
                            alt={expert.name}
                          />
                        </Avatar>
                      );
                    })}
                  </Box>
                ) : (
                  <Box
                    className="relative"
                    style={{ width: AVATAR_SLOT_SIZE, height: AVATAR_SLOT_SIZE }}>
                    <Avatar
                      size="sm"
                      className={twJoin(
                        'absolute h-10 w-10',
                        isPrimum && 'border-secondary-500/40 border'
                      )}
                      style={{ left: 0, top: 0 }}>
                      <AvatarImage
                        source={
                          effectiveAvatarUrl ? { uri: effectiveAvatarUrl } : primum_default_avatar
                        }
                        alt={nameWithoutDoctor || ''}
                      />
                    </Avatar>
                  </Box>
                )}
              </HStack>

              <VStack className="min-w-0 flex-1" style={{ marginLeft: 8 }}>
                <HStack className="items-start justify-between gap-2">
                  <HStack className="min-w-0 flex-1 items-center gap-2">
                    <SkeletonText
                      className={twJoin('h-6', typeBadge ? 'min-w-0 shrink' : 'flex-1')}
                      isLoaded={isUserLoaded}>
                      <Text
                        size="md"
                        numberOfLines={1}
                        className={twJoin(
                          showUnreadState
                            ? 'font-semibold text-typography-800'
                            : 'font-medium text-typography-700'
                        )}>
                        {!hideSubject ? subject : displayName}
                      </Text>
                    </SkeletonText>
                    {typeBadge ? (
                      <HStack testID="type-badge" className="shrink-0 items-center gap-1">
                        <Icon
                          as={typeBadge.icon}
                          size="2xs"
                          className={TYPE_BADGE_TONE[typeBadge.tone]}
                        />
                        <Text size="xs" className={TYPE_BADGE_TONE[typeBadge.tone]}>
                          {typeBadge.label}
                        </Text>
                      </HStack>
                    ) : null}
                  </HStack>
                  {!hideTimestamp ? (
                    <Text size="xs" className="shrink-0 text-typography-500">
                      {formattedDate}
                    </Text>
                  ) : null}
                </HStack>

                {!showSupportBadgeInline && !hideName ? (
                  <HStack className="mt-1 items-center justify-between gap-2">
                    <Text
                      size="sm"
                      className="min-w-0 flex-1 text-typography-700"
                      numberOfLines={1}>
                      {secondaryNameText}
                    </Text>
                    {statusBadge}
                  </HStack>
                ) : null}

                {routeLabels?.length ? (
                  <HStack testID="route-labels" className="mt-1 items-center gap-1.5">
                    <Text size="sm" className="text-typography-700" numberOfLines={1}>
                      {routeLabels.join(', ')}
                    </Text>
                  </HStack>
                ) : null}

                {showGroupedAnonymousHint ? (
                  <HStack className="mt-0.5 items-start gap-1.5">
                    <Icon as={HatGlasses} size="2xs" className="mt-1 text-info-700" />
                    <Text
                      size="xs"
                      className="min-w-0 flex-1 pt-0.5 text-info-700"
                      numberOfLines={2}>
                      {ANONYMOUS_PRIMUM_ROUTING_INBOX_HINT}
                    </Text>
                  </HStack>
                ) : null}
                {!showGroupedAnonymousHint && showSupportBadgeInline ? (
                  <Text size="xs" className="mt-0.5 text-info-700" numberOfLines={1}>
                    Primum Support
                  </Text>
                ) : null}

                <SkeletonText className="mt-1 h-6 w-2/3" isLoaded={isMessageLoaded}>
                  <Text
                    size="md"
                    numberOfLines={hideName ? 2 : 1}
                    className={twJoin(
                      showUnreadState ? 'font-medium text-typography-800' : 'text-typography-700'
                    )}
                    {...getNativeTextProps()}
                    isTruncated>
                    {parsedPreview.expertName
                      ? `${parsedPreview.expertName}: ${parsedPreview.previewText}`
                      : parsedPreview.previewText}
                  </Text>
                </SkeletonText>

                {productLabel ? (
                  <HStack testID="product-label" className="mt-1 items-center gap-1.5">
                    <View aria-hidden accessibilityElementsHidden>
                      <Icon as={Dna} size="2xs" className="shrink-0 text-typography-700" />
                    </View>
                    <Text size="xs" className="text-typography-800" numberOfLines={1}>
                      {productLabel}
                    </Text>
                  </HStack>
                ) : null}

                {visibleLabels.length ? (
                  <HStack className="mt-1 items-start justify-between gap-2">
                    <HStack className="min-w-0 flex-1 flex-wrap gap-1">
                      {visibleLabels.map((label) => (
                        <Badge
                          key={`${label.key}:${String(label.value)}`}
                          size="md"
                          variant="outline"
                          className="border-primary-200 bg-primary-50">
                          <BadgeText
                            size="sm"
                            numberOfLines={1}
                            className="normal-case text-primary-800">
                            {formatLabelText(label)}
                          </BadgeText>
                        </Badge>
                      ))}
                    </HStack>
                  </HStack>
                ) : null}
              </VStack>
            </HStack>
          ) : (
            <HStack className="items-start gap-2">
              <Box className="w-4 items-center self-center">
                <Box
                  testID={showUnreadState ? 'unread-indicator' : undefined}
                  className={twJoin(
                    'h-2.5 w-2.5',
                    showUnreadState && 'rounded-full bg-tertiary-400'
                  )}
                />
              </Box>
              {showGroupedExpertAvatars ? (
                <Box
                  className="relative"
                  style={{ width: AVATAR_SLOT_SIZE, height: AVATAR_SLOT_SIZE }}>
                  {visibleGroupExperts.map((expert, index) => {
                    const layout = getGroupedAvatarLayout(index, visibleGroupExperts.length);

                    return (
                      <Avatar
                        key={`${expert.name}-${index}-desktop-main`}
                        size="xs"
                        className="absolute"
                        style={{
                          left: layout.left,
                          top: layout.top,
                          width: layout.size,
                          height: layout.size,
                        }}>
                        <AvatarImage
                          source={
                            expert.avatarUrl ? { uri: expert.avatarUrl } : primum_default_avatar
                          }
                          alt={expert.name}
                        />
                      </Avatar>
                    );
                  })}
                </Box>
              ) : (
                <Box
                  className="relative"
                  style={{ width: AVATAR_SLOT_SIZE, height: AVATAR_SLOT_SIZE }}>
                  <Avatar
                    size="sm"
                    className={twJoin(
                      'absolute h-10 w-10',
                      isPrimum && 'border-secondary-500/40 border'
                    )}
                    style={{ left: 0, top: 0 }}>
                    <AvatarImage
                      source={
                        effectiveAvatarUrl ? { uri: effectiveAvatarUrl } : primum_default_avatar
                      }
                      alt={nameWithoutDoctor || ''}
                    />
                  </Avatar>
                </Box>
              )}

              <VStack className="flex-1">
                <HStack className="mb-1 items-center justify-between gap-2">
                  <HStack className="min-w-0 flex-1 items-center gap-2">
                    {!hideSubject ? (
                      <SkeletonText
                        className={twJoin('h-6', typeBadge ? 'min-w-0 shrink' : 'flex-1')}
                        isLoaded={isUserLoaded}>
                        <Text
                          size="md"
                          numberOfLines={1}
                          className="font-semibold text-typography-800">
                          {subject}
                        </Text>
                      </SkeletonText>
                    ) : (
                      <HStack className="flex-1" />
                    )}
                    {typeBadge ? (
                      <HStack testID="type-badge" className="shrink-0 items-center gap-1">
                        <Icon
                          as={typeBadge.icon}
                          size="2xs"
                          className={TYPE_BADGE_TONE[typeBadge.tone]}
                        />
                        <Text size="xs" className={TYPE_BADGE_TONE[typeBadge.tone]}>
                          {typeBadge.label}
                        </Text>
                      </HStack>
                    ) : null}
                  </HStack>
                  {!hideTimestamp ? (
                    <Text size="xs" className="text-typography-500">
                      {formattedDate}
                    </Text>
                  ) : null}
                </HStack>

                {!showSupportBadgeInline && !hideName ? (
                  <HStack className="mb-0.5 items-center justify-between gap-2">
                    <Text
                      size="sm"
                      numberOfLines={1}
                      className="min-w-0 flex-1 text-typography-700">
                      {secondaryNameText}
                    </Text>
                    {desktopStatusBadge}
                  </HStack>
                ) : null}

                {routeLabels?.length ? (
                  <HStack testID="route-labels-desktop" className="mb-0.5 items-center gap-1.5">
                    <Text size="sm" className="text-typography-700" numberOfLines={1}>
                      {routeLabels.join(', ')}
                    </Text>
                  </HStack>
                ) : null}

                {showGroupedAnonymousHint ? (
                  <HStack className="mb-0.5 items-start gap-1.5">
                    <Icon as={HatGlasses} size="2xs" className="mt-1 text-info-700" />
                    <Text
                      size="xs"
                      className="min-w-0 flex-1 pt-0.5 text-info-700"
                      numberOfLines={2}>
                      {ANONYMOUS_PRIMUM_ROUTING_INBOX_HINT}
                    </Text>
                  </HStack>
                ) : null}
                {!showGroupedAnonymousHint && showSupportBadgeInline ? (
                  <Text size="xs" className="mb-0.5 text-info-700" numberOfLines={1}>
                    Primum Support
                  </Text>
                ) : null}

                <SkeletonText className="h-10 w-2/3" isLoaded={isMessageLoaded}>
                  <Text
                    {...getNativeTextProps()}
                    size="sm"
                    className={twJoin(
                      'line-clamp-2',
                      showUnreadState ? 'font-medium text-typography-800' : 'text-typography-700'
                    )}>
                    {parsedPreview.expertName
                      ? `${parsedPreview.expertName}: ${parsedPreview.previewText}`
                      : parsedPreview.previewText}
                  </Text>
                </SkeletonText>

                {productLabel ? (
                  <HStack testID="product-label" className="mt-2 items-center gap-1.5">
                    <View aria-hidden accessibilityElementsHidden>
                      <Icon as={Dna} size="2xs" className="shrink-0 text-typography-700" />
                    </View>
                    <Text size="xs" className="text-typography-800" numberOfLines={1}>
                      {productLabel}
                    </Text>
                  </HStack>
                ) : null}

                {visibleLabels.length ? (
                  <HStack className="mt-1 items-start justify-between gap-2">
                    <HStack className="min-w-0 flex-1 flex-wrap gap-1">
                      {visibleLabels.map((label) => (
                        <Badge
                          key={`${label.key}:${String(label.value)}-desktop`}
                          size="md"
                          variant="outline"
                          className="border-primary-200 bg-primary-50">
                          <BadgeText
                            size="sm"
                            numberOfLines={1}
                            className="normal-case text-primary-800">
                            {formatLabelText(label)}
                          </BadgeText>
                        </Badge>
                      ))}
                    </HStack>
                  </HStack>
                ) : null}
              </VStack>
            </HStack>
          )}
        </VStack>
      </Card>
    </Pressable>
  );
}
